/**
 * G07/G16: ParcelMap Component
 * Leaflet-based map for parcel selection with multi-select support
 */

import L from 'leaflet'
// Leaflet CSS loaded via index.html from CDN for reliability
import type { ParcelResult, WGS84Coordinate } from './types'
import { uldkService } from './ULDKService'
import { areAllAdjacent } from './polygonMerge'
import { t } from '@/i18n'

export interface ParcelMapOptions {
  center?: WGS84Coordinate
  zoom?: number
  clickToSelectEnabled?: boolean
}

export class ParcelMap {
  private map: L.Map
  private selectedParcels: Map<string, ParcelResult> = new Map()
  private parcelLayers: Map<string, L.GeoJSON> = new Map()
  private clickToSelectEnabled: boolean = false
  private loadingOverlay: HTMLElement | null = null

  // Callbacks
  public onParcelSelected: ((parcel: ParcelResult) => void) | null = null
  public onParcelsChanged: ((parcels: ParcelResult[]) => void) | null = null
  public onAdjacencyWarning: ((isAdjacent: boolean) => void) | null = null
  public onError: ((error: Error) => void) | null = null
  public onLoading: ((loading: boolean) => void) | null = null

  constructor(container: HTMLElement, options: ParcelMapOptions = {}) {
    // Default to Poland center
    const center = options.center ?? { lat: 52.0, lon: 19.0 }
    const zoom = options.zoom ?? 6

    console.log('[ParcelMap] Initializing with container:', container.offsetWidth, 'x', container.offsetHeight)

    // Initialize Leaflet map
    this.map = L.map(container).setView([center.lat, center.lon], zoom)

    console.log('[ParcelMap] Map created, adding tile layer')

    // Add OSM tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map)

    console.log('[ParcelMap] Tile layer added')

    // Create loading overlay
    this.createLoadingOverlay(container)

    // Enable click-to-select if requested
    if (options.clickToSelectEnabled) {
      this.enableClickToSelect()
    }
  }

  // ============ Entry Points ============

  /**
   * Entry A: Search by address
   */
  async searchByAddress(address: string): Promise<void> {
    this.setLoading(true, t('map.modal.searchingAddress'))
    try {
      const result = await uldkService.getParcelByAddress(address)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      this.showParcel(result.value)
    } catch (error) {
      this.onError?.(error as Error)
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Entry B: Search by parcel ID
   */
  async searchByParcelId(parcelId: string): Promise<void> {
    this.setLoading(true, t('map.modal.findingParcel'))
    try {
      const result = await uldkService.getParcelById(parcelId)
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      this.showParcel(result.value)
    } catch (error) {
      this.onError?.(error as Error)
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Entry C: Enable click-to-select mode
   */
  enableClickToSelect(): void {
    if (this.clickToSelectEnabled) return
    this.clickToSelectEnabled = true
    this.map.on('click', this.handleMapClick)
    this.map.getContainer().style.cursor = 'crosshair'
  }

  disableClickToSelect(): void {
    if (!this.clickToSelectEnabled) return
    this.clickToSelectEnabled = false
    this.map.off('click', this.handleMapClick)
    this.map.getContainer().style.cursor = ''
  }

  // ============ Map Control ============

  setCenter(lat: number, lon: number, zoom?: number): void {
    if (zoom !== undefined) {
      this.map.setView([lat, lon], zoom)
    } else {
      this.map.panTo([lat, lon])
    }
  }

  clearSelection(): void {
    for (const [, layer] of this.parcelLayers) {
      this.map.removeLayer(layer)
    }
    this.parcelLayers.clear()
    this.selectedParcels.clear()
  }

  /** Backward compat: returns first selected parcel or null */
  getSelectedParcel(): ParcelResult | null {
    const first = this.selectedParcels.values().next()
    return first.done ? null : first.value
  }

  /** Get all selected parcels */
  getSelectedParcels(): ParcelResult[] {
    return Array.from(this.selectedParcels.values())
  }

  invalidateSize(): void {
    this.map.invalidateSize()
  }

  // ============ Lifecycle ============

  destroy(): void {
    this.disableClickToSelect()
    this.clearSelection()
    if (this.loadingOverlay) {
      this.loadingOverlay.remove()
    }
    this.map.remove()
  }

  // ============ Private Methods ============

  private handleMapClick = async (e: L.LeafletMouseEvent) => {
    const { lat, lng: lon } = e.latlng
    this.setLoading(true, t('map.modal.findingParcelAtLocation'))
    try {
      const result = await uldkService.getParcelByXY({ lat, lon })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      this.showParcel(result.value)
    } catch (error) {
      this.onError?.(error as Error)
    } finally {
      this.setLoading(false)
    }
  }

  private showParcel(parcel: ParcelResult): void {
    // Toggle: if already selected, remove it
    if (this.selectedParcels.has(parcel.id)) {
      this.removeParcel(parcel.id)
      return
    }

    // Add parcel to selection
    this.addParcelToMap(parcel)

    // Fit bounds to all selected parcels combined
    this.fitBoundsToAll()

    // Store and emit
    this.selectedParcels.set(parcel.id, parcel)
    this.onParcelSelected?.(parcel)
    this.emitParcelsChanged()
  }

  private addParcelToMap(parcel: ParcelResult): void {
    // Convert boundary to GeoJSON
    const coordinates = parcel.boundary.map(c => [c.lon, c.lat])
    // Close the polygon
    if (coordinates.length > 0) {
      coordinates.push(coordinates[0])
    }

    const geoJson: GeoJSON.Feature = {
      type: 'Feature',
      properties: { id: parcel.id },
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates]
      }
    }

    // Add to map with styling (using design colors)
    const layer = L.geoJSON(geoJson, {
      style: {
        color: '#92cc09',      // Primary green
        weight: 3,
        fillColor: '#92cc09',
        fillOpacity: 0.25
      }
    }).addTo(this.map)

    this.parcelLayers.set(parcel.id, layer)
    this.selectedParcels.set(parcel.id, parcel)
  }

  private removeParcel(id: string): void {
    const layer = this.parcelLayers.get(id)
    if (layer) {
      this.map.removeLayer(layer)
      this.parcelLayers.delete(id)
    }
    this.selectedParcels.delete(id)
    this.emitParcelsChanged()

    // Re-fit bounds if parcels remain
    if (this.parcelLayers.size > 0) {
      this.fitBoundsToAll()
    }
  }

  private fitBoundsToAll(): void {
    const allBounds = L.latLngBounds([])
    for (const [, layer] of this.parcelLayers) {
      allBounds.extend(layer.getBounds())
    }
    if (allBounds.isValid()) {
      this.map.fitBounds(allBounds, { padding: [50, 50] })
    }
  }

  private emitParcelsChanged(): void {
    const parcels = this.getSelectedParcels()
    this.onParcelsChanged?.(parcels)

    // Check adjacency when 2+ parcels selected
    if (parcels.length >= 2) {
      const adjacent = areAllAdjacent(parcels.map(p => p.boundaryPl2000))
      this.onAdjacencyWarning?.(adjacent)
    } else {
      this.onAdjacencyWarning?.(true)
    }
  }

  private createLoadingOverlay(container: HTMLElement): void {
    this.loadingOverlay = document.createElement('div')
    this.loadingOverlay.className = 'parcel-map-loading'
    this.loadingOverlay.innerHTML = `
      <div class="parcel-map-loading-content">
        <div class="parcel-map-spinner"></div>
        <div class="parcel-map-loading-text">Loading...</div>
      </div>
    `
    this.loadingOverlay.style.display = 'none'
    container.style.position = 'relative'
    container.appendChild(this.loadingOverlay)
  }

  private setLoading(loading: boolean, message?: string): void {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = loading ? 'flex' : 'none'
      if (message) {
        const textEl = this.loadingOverlay.querySelector('.parcel-map-loading-text')
        if (textEl) {
          textEl.textContent = message
        }
      }
    }
    this.onLoading?.(loading)
  }
}
